import React from "react";
import { Box, ButtonBase, InputBase, Typography } from "@mui/material";
import { RiCheckboxBlankCircleLine, RiCloseLine, RiEyeLine, RiGroupLine, RiRadioButtonLine, RiUserSearchLine } from "@remixicon/react";
import { focusRing, tokens } from "../../utils/figmaTokens";
import { fillName } from "../../utils/announcementPresentation";
import { lockScreen } from "../../utils/announcementForm";
import InitialsAvatar from "../../components/kit/InitialsAvatar";
import phonePhoto from "../../assets/notifications/phone-frame-photo.png";
import appLogo from "../../assets/notifications/app-logo-mark.svg";

// The New notification page's pieces (spec 044 D41–D48), drawn from Figma's Compose screens
// (11330:91691, 11330:92901). The chosen audience's dark tile is surface/inverse, and the purple
// match line is brand/accent with text/accent-1 (047 P131).

// A section's head: "Choose the audience" (21/28) or a card-less "Notice content" (16/26).
export const SectionHead = ({ title, subtitle, large = false }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
    <Typography component="h2" sx={{ fontSize: large ? 21 : 16, lineHeight: large ? "28px" : "26px", fontWeight: large ? 600 : 500, letterSpacing: large ? "-0.2px" : 0, color: tokens.text.primary }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: large ? 16 : 14, lineHeight: large ? "26px" : "22px", color: tokens.text.secondary }}>{subtitle}</Typography>
  </Box>
);

// The white cards: the Choose-students card (radius 16, padding 18) and the side cards (radius
// 20, padding 24).
export const Card = ({ children, sx }) => (
  <Box sx={{ bgcolor: tokens.surface.default, border: `1px solid ${tokens.border.default}`, borderRadius: "16px", p: "18px", display: "flex", flexDirection: "column", gap: "14px", ...sx }}>
    {children}
  </Box>
);

const pressable = (selected, disabled) => ({
  textAlign: "left",
  fontFamily: "inherit",
  opacity: disabled ? 0.6 : 1,
  cursor: disabled ? "default" : "pointer",
  "&:hover": disabled || selected ? {} : { bgcolor: tokens.hover },
  ...focusRing,
});

// An audience card (Students / Teachers): a 46 px icon tile, the name and a line under it. The
// chosen one wears the 1.5 px dark border and the dark tile.
export const AudienceCard = ({ Icon, title, subtitle, selected, onClick, disabled }) => (
  <ButtonBase
    role="radio"
    aria-checked={selected}
    onClick={onClick}
    disabled={disabled}
    disableRipple
    sx={{
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "2px",
      p: "16px",
      borderRadius: "14px",
      bgcolor: tokens.surface.default,
      border: selected ? `1.5px solid ${tokens.buttonBorder}` : `1px solid ${tokens.border.default}`,
      m: selected ? 0 : "0.5px",
      ...pressable(selected, disabled),
    }}
  >
    <Box sx={{ width: 46, height: 46, borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: selected ? tokens.surface.inverse : tokens.surface.muted, color: selected ? tokens.text.inverse : tokens.icon.default }}>
      <Icon size={24} />
    </Box>
    <Typography sx={{ fontSize: 16, lineHeight: "26px", fontWeight: 500, color: tokens.text.primary }}>{title}</Typography>
    <Typography sx={{ fontSize: 14, lineHeight: "22px", color: selected ? tokens.text.tertiary : tokens.text.secondary }}>{subtitle}</Typography>
  </ButtonBase>
);

// A radio row (All students / Specific students; Send now / Schedule for later): the radio
// glyph, a title and a line under it. The chosen one is blue-tinted with a brand border; the
// text keeps its ink (screen 2's treatment). `strong` is the send-settings pair's 14/20 title.
export const ChoiceRow = ({ title, subtitle, selected, onClick, disabled, strong = false }) => (
  <ButtonBase
    role="radio"
    aria-checked={selected}
    onClick={onClick}
    disabled={disabled}
    disableRipple
    sx={{
      width: "100%",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      px: "16px",
      py: strong ? "14px" : "13px",
      borderRadius: strong ? "14px" : "12px",
      bgcolor: selected ? tokens.brand.subtle : tokens.surface.default,
      border: selected ? `1.5px solid ${tokens.brand.border}` : `1.5px solid ${tokens.border.default}`,
      ...pressable(selected, disabled),
    }}
  >
    <Box sx={{ display: "flex", color: selected ? tokens.icon.brand : tokens.icon.secondary, flexShrink: 0 }}>
      {selected ? <RiRadioButtonLine size={24} /> : <RiCheckboxBlankCircleLine size={24} />}
    </Box>
    <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "1px" }}>
      <Typography sx={strong ? { fontSize: 14, lineHeight: "20px", fontWeight: 600, letterSpacing: "0.1px", color: tokens.text.primary } : { fontSize: 16, lineHeight: "26px", color: tokens.text.primary }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 14, lineHeight: "22px", color: tokens.text.secondary }}>{subtitle}</Typography>
    </Box>
  </ButtonBase>
);

// A field's label row: the label on the left, an optional "31 / 65" on the right.
export const FieldLabel = ({ id, label, counter, strong = true }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
    <Typography component="label" htmlFor={id} sx={strong ? { fontSize: 14, lineHeight: "20px", fontWeight: 500, letterSpacing: "0.1px", color: tokens.text.primary } : { fontSize: 14, lineHeight: "22px", color: tokens.text.secondary }}>
      {label}
    </Typography>
    {counter && (
      <Typography variant="labelMedium" component="span" sx={{ flex: 1, textAlign: "right", color: tokens.text.tertiary }}>
        {counter}
      </Typography>
    )}
  </Box>
);

// The form's inputs (Input · title, Textarea · message, Select · Age, the Date and Time fields):
// a 12 px radius, a 1 px border that turns 1.5 px dark while focused, drawn inside so nothing
// moves. `large` is the 16/26 text of the title and message; the rest are 14/22 with an icon.
export const Field = ({ Icon, EndIcon, large = false, sx, inputSx, ...rest }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: rest.multiline ? "flex-start" : "center",
      gap: "10px",
      px: large ? "16px" : "14px",
      py: large ? (rest.multiline ? "16px" : "14px") : "12px",
      borderRadius: "12px",
      bgcolor: tokens.surface.default,
      boxShadow: `inset 0 0 0 1px ${tokens.border.default}`,
      color: tokens.icon.default,
      "&:focus-within": { boxShadow: `inset 0 0 0 1.5px ${tokens.buttonBorder}` },
      ...sx,
    }}
  >
    {Icon && <Icon size={18} aria-hidden />}
    <InputBase
      fullWidth
      sx={{
        flex: 1,
        minWidth: 0,
        fontSize: large ? 16 : 14,
        lineHeight: large ? "26px" : "22px",
        color: tokens.text.primary,
        "& input, & textarea": { p: 0, height: large ? 26 : 22 },
        "& textarea": { height: "auto" },
        "& input::placeholder, & textarea::placeholder": { color: tokens.text.tertiary, opacity: 1 },
        "&.Mui-disabled": { color: tokens.text.secondary },
        ...inputSx,
      }}
      {...rest}
    />
    {EndIcon && <EndIcon size={large ? 20 : 18} aria-hidden />}
  </Box>
);

// The toolbar's pill tools under the message ("Add user's name", "Add link"): a 16 px icon and a
// 12/16 label in a 10 px-radius outline.
export const ToolButton = ({ Icon, children, onClick, disabled, pressed }) => (
  <ButtonBase
    onClick={onClick}
    disabled={disabled}
    aria-pressed={pressed}
    disableRipple
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      px: "14px",
      py: "9px",
      borderRadius: "10px",
      border: `1px solid ${tokens.border.strong}`,
      bgcolor: pressed ? tokens.surface.muted : tokens.surface.default,
      color: tokens.text.secondary,
      fontSize: 12,
      lineHeight: "16px",
      fontWeight: 500,
      letterSpacing: "0.2px",
      fontFamily: "inherit",
      "&:hover": { bgcolor: tokens.hover },
      "&.Mui-disabled": { color: tokens.text.disabled },
      ...focusRing,
    }}
  >
    <Icon size={16} aria-hidden />
    {children}
  </ButtonBase>
);

// The gender pills (Segmented): a grey track, the chosen pill solid brand with white text.
export const Segmented = ({ options, value, onChange, disabled, label }) => (
  <Box role="radiogroup" aria-label={label} sx={{ display: "flex", gap: "4px", p: "4px", borderRadius: "999px", bgcolor: tokens.surface.muted, opacity: disabled ? 0.6 : 1 }}>
    {options.map((o) => {
      const selected = o.key === value;
      return (
        <ButtonBase
          key={o.key}
          role="radio"
          aria-checked={selected}
          onClick={() => onChange(o.key)}
          disabled={disabled}
          disableRipple
          sx={{
            flex: 1,
            gap: "6px",
            pl: "10px",
            pr: "12px",
            borderRadius: "999px",
            fontFamily: "inherit",
            fontSize: 14,
            lineHeight: selected ? "20px" : "22px",
            fontWeight: selected ? 600 : 400,
            letterSpacing: selected ? "0.1px" : 0,
            bgcolor: selected ? tokens.brand.primary : "transparent",
            color: selected ? tokens.brand.on : tokens.text.secondary,
            py: selected ? "9px" : "8px",
            ...focusRing,
          }}
        >
          {o.Icon && <o.Icon size={16} aria-hidden />}
          {o.label}
        </ButtonBase>
      );
    })}
  </Box>
);

// The quick-pick chips (Tomorrow, 09:00 …): white pills with a hairline.
export const PickChip = ({ children, onClick, disabled }) => (
  <ButtonBase
    onClick={onClick}
    disabled={disabled}
    disableRipple
    sx={{
      px: "14px",
      py: "8px",
      borderRadius: "999px",
      border: `1px solid ${tokens.border.default}`,
      bgcolor: tokens.surface.default,
      color: tokens.text.secondary,
      fontFamily: "inherit",
      fontSize: 14,
      lineHeight: "22px",
      "&:hover": { bgcolor: tokens.hover },
      "&.Mui-disabled": { color: tokens.text.disabled },
      ...focusRing,
    }}
  >
    {children}
  </ButtonBase>
);

// A chosen person (Selected students): a 24 px avatar, the name, a × to remove.
export const PersonChip = ({ person, onRemove, disabled }) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "8px", p: "8px", borderRadius: "999px", border: `1px solid ${tokens.border.strong}`, bgcolor: tokens.surface.default }}>
    <InitialsAvatar name={person.name || person.email || "?"} colorKey={person.id} size={24} sx={{ fontSize: 11, lineHeight: "16px" }} />
    <Typography component="span" sx={{ fontSize: 14, lineHeight: "20px", fontWeight: 500, letterSpacing: "0.25px", color: tokens.text.primary }}>
      {person.name || person.email || person.id}
    </Typography>
    <ButtonBase aria-label={`Remove ${person.name || person.email || "person"}`} onClick={onRemove} disabled={disabled} disableRipple sx={{ p: "2px", borderRadius: "999px", color: tokens.icon.default, "&.Mui-disabled": { color: tokens.text.disabled }, ...focusRing }}>
      <RiCloseLine size={16} />
    </ButtonBase>
  </Box>
);

// The purple match line: "312 students match — male, ages 10 – 13".
export const MatchLine = ({ children, action }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "10px", px: "14px", py: "12px", borderRadius: "12px", bgcolor: tokens.accent.bg, color: tokens.accent.ink }} role="status">
    <RiGroupLine size={18} aria-hidden />
    <Typography sx={{ flex: 1, fontSize: 14, lineHeight: "22px", color: "inherit" }}>{children}</Typography>
    {action}
  </Box>
);

// The preview card: the eye tile, the words, then the phone (322 × 380) with the lock screen
// and the notification bubble drawn over the photo. The photo fades out towards the bottom
// (Figma's luminance mask is a plain white-to-black gradient, so a CSS gradient mask is the
// same drawing). `at` is the moment it arrives; `first` is the sample first name.
const fade = `linear-gradient(to bottom, ${tokens.phone.mask} 0%, transparent 342px)`;
export const PhonePreview = ({ line, title, body, at, first, timeZone }) => {
  const clock = lockScreen(at, { timeZone });
  return (
    <Card sx={{ borderRadius: "20px", p: "24px", gap: "16px", alignItems: "center" }}>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", p: "24px", textAlign: "center" }}>
        <Box sx={{ p: "8px", borderRadius: "8px", bgcolor: tokens.surface.muted, color: tokens.icon.default, display: "flex" }}>
          <RiEyeLine size={24} aria-hidden />
        </Box>
        <Typography component="h2" sx={{ fontSize: 16, lineHeight: "24px", fontWeight: 600, letterSpacing: "-0.1px", color: tokens.text.primary }}>
          Preview the notification
        </Typography>
        <Typography sx={{ fontSize: 14, lineHeight: "22px", color: tokens.text.secondary }}>{line}</Typography>
      </Box>
      <Box sx={{ position: "relative", width: 322, maxWidth: "100%", height: 380, overflow: "hidden" }} aria-hidden>
        <Box
          component="img"
          src={phonePhoto}
          alt=""
          sx={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: 322, height: 661, objectFit: "cover", maskImage: fade, WebkitMaskImage: fade }}
        />
        <Box sx={{ position: "absolute", top: 42, right: 15, width: 293, height: 312, p: "12px", display: "flex", flexDirection: "column", gap: "10px", alignItems: "center", maskImage: fade, WebkitMaskImage: fade, maskPosition: "0 -42px", WebkitMaskPosition: "0 -42px" }}>
          <Box sx={{ py: "16px", textAlign: "center", color: tokens.phone.ink, whiteSpace: "nowrap" }}>
            <Typography sx={{ fontSize: 14, lineHeight: "22px", opacity: 0.85, color: "inherit" }}>{clock.day}</Typography>
            <Typography sx={{ fontSize: 67, lineHeight: "72px", fontWeight: 600, letterSpacing: "-1.3px", color: "inherit" }}>{clock.time}</Typography>
          </Box>
          <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: "7px", p: "12px", borderRadius: "12px", bgcolor: tokens.surface.default }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "8px", bgcolor: tokens.brand.primary, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Box component="img" src={appLogo} alt="" sx={{ width: 30, height: 14 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
                <Typography noWrap sx={{ flex: 1, fontSize: 12, lineHeight: "16px", fontWeight: 500, letterSpacing: "0.2px", color: tokens.text.primary }}>
                  {fillName(title, first) || "Title"}
                </Typography>
                <Typography sx={{ fontSize: 10, lineHeight: "14px", fontWeight: 500, letterSpacing: "0.3px", color: tokens.text.tertiary }}>now</Typography>
              </Box>
              <Typography sx={{ fontSize: 10, lineHeight: "14px", letterSpacing: "0.3px", color: tokens.text.secondary, height: 42, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", whiteSpace: "pre-line" }}>
                {fillName(body, first) || "Message"}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

// The Audience summary card: label / value rows, a hairline, then the Recipients pill (purple,
// screen 2's, everywhere).
export const SummaryCard = ({ rows, recipients, action }) => (
  <Card sx={{ borderRadius: "20px", p: "20px 24px 22px" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <Typography component="h2" sx={{ flex: 1, fontSize: 18, lineHeight: "30px", fontWeight: 500, color: tokens.text.primary }}>
        Audience summary
      </Typography>
      <Box sx={{ display: "flex", color: tokens.accent.ink }}>
        <RiUserSearchLine size={20} aria-hidden />
      </Box>
    </Box>
    {rows.map((r) => (
      <Box key={r.label} sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <Typography sx={{ flex: 1, fontSize: 14, lineHeight: "22px", color: tokens.text.secondary }}>{r.label}</Typography>
        <Typography sx={{ fontSize: 14, lineHeight: "22px", fontWeight: 500, color: tokens.text.primary, whiteSpace: "nowrap" }}>{r.value}</Typography>
      </Box>
    ))}
    <Box sx={{ height: "1px", bgcolor: tokens.border.subtle }} />
    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <Typography sx={{ flex: 1, fontSize: 14, lineHeight: "22px", color: tokens.text.secondary }}>Recipients</Typography>
      {action}
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px", pl: "10px", pr: "12px", py: "5px", borderRadius: "999px", bgcolor: tokens.accent.bg, color: tokens.accent.ink, fontSize: 14, lineHeight: "22px", fontWeight: 500, whiteSpace: "nowrap" }}>
        <RiGroupLine size={15} aria-hidden />
        {recipients}
      </Box>
    </Box>
  </Card>
);
